import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Puente, RecursoPuente, TipoPuente } from '@api/Puente/Puente';
import PuenteController from '@api/Puente/PuenteCtrl';
import BrandLogo from '@src/components/BrandLogo';
import StoreBadges from '@src/components/StoreBadges';
import { detectarPlataforma, intentAndroid } from '@src/shared/utils/stores';

/**
 * Página puente (oct/2026, «App primero»): el ticket, la reservación, la cita y la gift card ya
 * no se muestran en la web, se ven en la app. Con la app 1.0.39 o posterior el link de WhatsApp
 * o del correo abre la app directo y esta página ni se ve; llega aquí quien no tiene la app o
 * la tiene vieja. Decisión de Irving (8/oct/2026): solo invitar a descargar, sin confirmar nada
 * del pedido; el mensaje que la trajo ya es el comprobante.
 */

interface Textos {
    etiqueta: string;
    titulo: string;
    detalle: string;
    /** Dónde lo encuentra al entrar (el link se pierde al descargar la app). */
    dondeEsta: string;
}

const TEXTOS: Record<TipoPuente, Textos> = {
    ticket: {
        etiqueta: 'TU TICKET',
        titulo: 'Tu ticket está en la app Kabandú',
        detalle: 'Ahí ves el detalle de tu compra, tus Pesos Kabandú y tus próximas citas.',
        dondeEsta: 'Lo encuentras en Inicio → Mis pedidos.'
    },
    reservacion: {
        etiqueta: 'TU RESERVACIÓN',
        titulo: 'Tu reservación está en la app Kabandú',
        detalle: 'Ahí ves tu anticipo, lo que te falta por pagar y la fecha de tu cita.',
        dondeEsta: 'La encuentras en Inicio → Mis pedidos.'
    },
    cita: {
        etiqueta: 'TU CITA',
        titulo: 'Tu cita está en la app Kabandú',
        detalle: 'Ahí ves la fecha, la sucursal y los servicios de tu cita.',
        dondeEsta: 'La encuentras en Inicio → Mis pedidos.'
    },
    'gift-card': {
        etiqueta: 'TU GIFT CARD',
        titulo: 'Tu gift card está en la app Kabandú',
        detalle: 'Tu regalo ya está sumado a tus Pesos Kabandú: úsalo en tu próxima visita, en cualquier sucursal.',
        dondeEsta: 'Lo ves en Inicio → Tus Pesos Kabandú.'
    },
    // El expediente de Studio (`/consent/:id`): fuera de la app no se firma (plan del contrato).
    contrato: {
        etiqueta: 'TU CONTRATO',
        titulo: 'Firma tu contrato en la app Kabandú',
        detalle:
            'Antes de tu cita de Studio llenas tu historial clínico y firmas tu consentimiento desde la app. Si no alcanzas, lo firmas en la sucursal el día de tu cita.',
        dondeEsta: 'Lo encuentras en Inicio, en el aviso «Tienes un contrato por firmar».'
    }
};

/** Si el server no responde o el link no existe: la invitación sigue sirviendo. */
const GENERICO: Textos = {
    etiqueta: 'APP KABANDÚ',
    titulo: 'Tus tickets y citas están en la app Kabandú',
    detalle: 'Ahí ves tus compras, tus próximas citas y tus Pesos Kabandú.',
    dondeEsta: 'Los encuentras en Inicio → Mis pedidos.'
};

/** Etiqueta de sección: mayúsculas espaciadas, el gesto tipográfico de la marca. */
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="overline" color="text.secondary" display="block">
        {children}
    </Typography>
);

const Paso: React.FC<{ numero: number; titulo: React.ReactNode; children?: React.ReactNode }> = ({
    numero,
    titulo,
    children
}) => (
    <Stack direction="row" spacing={1.75} alignItems="flex-start">
        <Box
            sx={{
                flexShrink: 0,
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: '0.85rem'
            }}
        >
            {numero}
        </Box>
        <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0, pt: '3px' }}>
            <Typography fontWeight={600}>{titulo}</Typography>
            {children}
        </Stack>
    </Stack>
);

const PuenteScreen: React.FC<{ recurso: RecursoPuente }> = ({ recurso }) => {
    const { id } = useParams<{ id: string }>();
    const { pathname } = useLocation();
    const puenteController = useMemo(() => new PuenteController(), []);
    const plataforma = useMemo(() => detectarPlataforma(), []);
    const [puente, setPuente] = useState<Puente | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) {
            setLoading(false);
            return;
        }
        puenteController
            .getPuente(recurso, id)
            .then(setPuente)
            // Sin datos la página sigue invitando a descargar: no hay pantalla de error.
            .catch(() => setPuente(null))
            .finally(() => setLoading(false));
    }, [id, recurso, puenteController]);

    if (loading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    const textos = puente ? TEXTOS[puente.tipo] : GENERICO;
    const esGiftCard = puente?.tipo === 'gift-card';

    return (
        <Stack spacing={2.5} sx={{ maxWidth: 560, mx: 'auto', pt: { xs: 1, sm: 2 }, pb: 2 }}>
            <Stack spacing={1.25} alignItems="center" textAlign="center">
                <BrandLogo size={40} showName={false} justify="center" />
                <SectionTitle>{textos.etiqueta}</SectionTitle>
                <Typography variant="h4" component="h1">
                    {textos.titulo}
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 440 }}>
                    {textos.detalle}
                </Typography>
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
                {puente?.tieneApp ? (
                    // Si tiene la app y el link no la abrió, es una versión anterior a los links
                    // (≤ 1.0.36): «descárgala» no le dice nada, «actualízala» sí.
                    <Stack spacing={2}>
                        <Stack spacing={0.5}>
                            <SectionTitle>YA TIENES LA APP</SectionTitle>
                            <Typography>
                                Actualízala para que este enlace la abra directo. {textos.dondeEsta}
                            </Typography>
                        </Stack>
                        {plataforma === 'android' && (
                            <Button
                                href={intentAndroid(pathname)}
                                variant="contained"
                                size="large"
                                startIcon={<OpenInNewRoundedIcon />}
                            >
                                Abrir en la app
                            </Button>
                        )}
                        <StoreBadges plataforma={plataforma} centrado={false} />
                    </Stack>
                ) : (
                    <Stack spacing={2.5}>
                        <Paso numero={1} titulo="Descarga la app">
                            <StoreBadges plataforma={plataforma} centrado={false} />
                        </Paso>
                        <Divider />
                        <Paso
                            numero={2}
                            titulo={
                                puente?.telefonoFinal ? (
                                    <>
                                        Entra con tu número terminado en{' '}
                                        <Box component="span" sx={{ whiteSpace: 'nowrap', letterSpacing: '0.06em' }}>
                                            {puente.telefonoFinal}
                                        </Box>
                                    </>
                                ) : (
                                    'Entra con el número en el que recibiste este mensaje'
                                )
                            }
                        >
                            <Typography variant="body2" color="text.secondary">
                                {esGiftCard
                                    ? 'Es el número al que llegó tu regalo. Con otro número la cuenta empieza vacía, sin tu gift card.'
                                    : puente?.tipo === 'contrato'
                                    ? 'Es el número de tu cita. Con otro número la cuenta empieza vacía y no verás tu contrato.'
                                    : 'Es el número de tu cuenta, con tu historial y tus Pesos Kabandú. Con otro número la cuenta empieza vacía.'}{' '}
                                {textos.dondeEsta}
                            </Typography>
                        </Paso>
                    </Stack>
                )}
            </Paper>
        </Stack>
    );
};

export default PuenteScreen;
